import type { ScoutDashboardMetricsQuery } from "generated-graphql/graphql";

import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";

type ScoutDashboardWidget =
  ScoutDashboardMetricsQuery["scoutDashboardMetrics"]["statWidgets"][number];

const METRIC_BADGE_DESCRIPTIONS: Record<string, string> = {
  AWS: "A native AWS service metric published directly into an AWS CloudWatch namespace.",
  Encamp:
    "An Encamp-defined metric produced from 60 second rollup logs that are converted into a CloudWatch metric.",
};

function ScoutMetricDocumentationTooltipContent({
  documentation,
}: {
  documentation: ScoutDashboardWidget["documentation"];
}) {
  return (
    <Stack component="ul" spacing={0.5} sx={{ maxWidth: 420, m: 0, py: 0.5, pl: 2 }}>
      {documentation.details.map((detail) => (
        <Typography key={detail} component="li" variant="caption">
          {detail}
        </Typography>
      ))}
    </Stack>
  );
}

function ScoutMetricBadge({ badge }: { badge: string }) {
  return (
    <Tooltip
      arrow
      placement="top"
      title={METRIC_BADGE_DESCRIPTIONS[badge] ?? "Metric source category."}
    >
      <Chip
        label={badge}
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          maxWidth: 132,
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        variant="outlined"
      />
    </Tooltip>
  );
}

function ScoutMetricDocumentationIcon({
  title,
  widgetId,
  documentation,
}: {
  title: string;
  widgetId: string;
  documentation: ScoutDashboardWidget["documentation"];
}) {
  return (
    <Tooltip
      arrow
      placement="top"
      title={<ScoutMetricDocumentationTooltipContent documentation={documentation} />}
    >
      <IconButton
        size="small"
        aria-label={`About ${title} metric`}
        data-testid={`scout-metric-info-${widgetId}`}
        sx={{ color: "text.secondary", p: 0.25 }}
      >
        <InfoOutlined fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

export function ScoutMetricDocumentationHeader({
  widget,
  titleVariant,
}: {
  widget: ScoutDashboardWidget;
  titleVariant: "body2" | "subtitle1";
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0} sx={{ flex: 1 }}>
      <Tooltip title={widget.title}>
        <Typography
          variant={titleVariant}
          color={titleVariant === "body2" ? "text.secondary" : undefined}
          fontWeight={titleVariant === "subtitle1" ? 600 : undefined}
          noWrap
          sx={{ minWidth: 0 }}
        >
          {widget.title}
        </Typography>
      </Tooltip>
      <ScoutMetricDocumentationIcon
        title={widget.title}
        widgetId={widget.id}
        documentation={widget.documentation}
      />
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" minWidth={0}>
        {widget.documentation.badges.map((badge) => (
          <ScoutMetricBadge key={badge} badge={badge} />
        ))}
      </Stack>
    </Stack>
  );
}
